// Single source of truth for the transactions CSV export.
//
// Both the dashboard "Download transactions" link (PollingStatusMonitor) and the
// individual-index Reports export MUST use these helpers so their output can never
// drift apart again. The pipeline is: raw API transactions -> paired trades ->
// streaks attached -> CSV string. Do not re-implement any of these steps elsewhere.

const ENTRY_TYPES = new Set(['BUY', 'SHORT SELL']);
const EXIT_TYPES = new Set(['SELL', 'SELL (Forced)', 'SHORT COVER', 'SHORT COVER (Forced)']);

// ISO (YYYY-MM-DD) -> DD-MM-YYYY, identical to Reports.js formatSingleDate.
export const formatTradeDate = (dateStr) => {
  if (!dateStr) return '';
  const trimmed = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const [y, m, d] = trimmed.substring(0, 10).split('-');
    return `${d}-${m}-${y}`;
  }
  return trimmed;
};

// Pair a flat transaction list (BUY/SELL as separate rows) into one row per trade,
// folding in the currently-open trade from `openTrade`. Mirrors Reports.js allPairs.
export const pairTransactions = (transactions, openTrade) => {
  const sorted = [...(transactions || [])].sort((a, b) => a.trade_number - b.trade_number);
  const pairs = [];
  for (const cur of sorted) {
    if (!ENTRY_TYPES.has(cur.type)) continue;
    const isShort = cur.type === 'SHORT SELL';
    const exit = sorted.find(t => t.trade_number === cur.trade_number && EXIT_TYPES.has(t.type));
    let daysHeld = exit?.days_held || null;
    if (!exit && cur.date && openTrade?.last_close_date) {
      daysHeld = Math.floor((new Date(openTrade.last_close_date) - new Date(cur.date)) / 86400000);
    }
    pairs.push({
      trade_number: cur.trade_number,
      direction: isShort ? 'SHORT' : 'LONG',
      model_revision: cur.model_revision,
      entry_date: cur.date,
      entry_price: cur.price,
      exit_date: exit?.date,
      exit_price: exit?.price,
      exit_type: exit?.type,
      gain: exit?.gain ?? null,
      cumulative_gain: exit?.cumulative_gain ?? cur.cumulative_gain,
      days_held: daysHeld,
      mae: exit?.mae ?? (!exit && openTrade ? openTrade.mae : null),
      is_open: !exit,
      last_close: !exit ? openTrade?.last_close : null,
      last_close_date: !exit ? openTrade?.last_close_date : null,
    });
  }
  return pairs;
};

// Attach a single `streak_value` (the running compounded loss OR gain streak, in %)
// to each paired trade. Matches the Reports.js streak algorithm exactly: the first
// trade in a streak seeds the multiplier and shows null; the 2nd+ shows the running
// total. Input must be in chronological (ascending trade) order.
export const withStreaks = (pairs) => {
  let lossMult = 1, inLoss = false, gainMult = 1, inGain = false;
  return (pairs || []).map((trade, idx) => {
    const isLoss = trade.gain !== null && trade.gain !== undefined && trade.gain < 0;
    const isGain = trade.gain !== null && trade.gain !== undefined && trade.gain > 0;
    const prev = idx > 0 ? pairs[idx - 1] : null;
    const prevLoss = prev && prev.gain !== null && prev.gain < 0;
    const prevGain = prev && prev.gain !== null && prev.gain > 0;

    let lossStreak = null, gainStreak = null;
    if (isLoss) {
      lossMult = prevLoss ? lossMult * (1 + trade.gain / 100) : (1 + trade.gain / 100);
      inLoss = prevLoss;
      lossStreak = inLoss ? (lossMult - 1) * 100 : null;
      gainMult = 1; inGain = false;
    } else {
      lossMult = 1; inLoss = false;
    }
    if (isGain) {
      gainMult = prevGain ? gainMult * (1 + trade.gain / 100) : (1 + trade.gain / 100);
      inGain = prevGain;
      gainStreak = inGain ? (gainMult - 1) * 100 : null;
    } else if (!isLoss) {
      gainMult = 1; inGain = false;
    }
    return { ...trade, streak_value: lossStreak ?? gainStreak };
  });
};

const TRANSACTIONS_HEADER = 'Trade #,Direction,Entry Date,Exit Date,Entry Price,Exit Price,Gain %,Streak,Days Held,MAE %';

// Build the transactions CSV string from paired trades. Newest trade first, quoted
// fields — identical to what the individual-index export produces.
export const buildTransactionsCsv = (pairs) => {
  const rows = [...(pairs || [])].reverse().map(t => {
    const direction = t.direction || 'LONG';
    const entryDate = t.entry_date ? formatTradeDate(t.entry_date) : '';
    const exitDate = t.is_open ? 'OPEN' : (t.exit_date ? formatTradeDate(t.exit_date) : '');
    const entryPrice = t.entry_price != null ? t.entry_price.toFixed(2) : '';
    const exitPrice = t.is_open
      ? (t.last_close != null ? t.last_close.toFixed(2) : '')
      : (t.exit_price != null ? t.exit_price.toFixed(2) : '');
    const gain = t.gain != null
      ? `${t.gain > 0 ? '+' : ''}${t.gain.toFixed(2)}%`
      : (t.is_open && t.entry_price && t.last_close
        ? `${((t.last_close - t.entry_price) / t.entry_price * 100) > 0 ? '+' : ''}${((t.last_close - t.entry_price) / t.entry_price * 100).toFixed(2)}%`
        : '');
    const streak = t.streak_value != null ? `${t.streak_value > 0 ? '+' : ''}${t.streak_value.toFixed(2)}%` : '';
    const days = t.days_held != null ? t.days_held : '';
    const mae = t.mae != null ? `${t.mae.toFixed(2)}%` : '';
    return `"${t.trade_number}","${direction}","${entryDate}","${exitDate}","${entryPrice}","${exitPrice}","${gain}","${streak}","${days}","${mae}"`;
  });
  return TRANSACTIONS_HEADER + '\n' + rows.join('\n');
};

// `<symbol>_<strategy>[_<exec>][_<source>]_trades_all_<yyyy-mm-dd>.csv`.
// `source` ('published'|'latest') is included only when passed, so the individual
// export (no source arg) keeps its exact filename while the dashboard, which offers
// both, gets distinguishable files.
export const transactionsFilename = (symbolName, strategy, executionMode, today, source) => {
  const strat = strategy === 'with_shorts' ? 'with_shorts' : '1x';
  const execSuffix = executionMode && executionMode !== 'same_day_close' ? `_${executionMode}` : '';
  const srcSuffix = source ? `_${source}` : '';
  const day = today || new Date().toISOString().slice(0, 10);
  return `${symbolName}_${strat}${execSuffix}${srcSuffix}_trades_all_${day}.csv`;
};

// Trigger a browser download of a CSV string. The blob/anchor boilerplate lived in
// ~4 copies; this is the only copy now.
export const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// One-call convenience: raw API response ({ transactions, open_trade }) -> download.
// Used by both call sites so the whole pipeline stays identical.
export const downloadTransactionsCsv = (apiData, { symbolName, strategy, executionMode, today, source } = {}) => {
  const pairs = withStreaks(pairTransactions(apiData?.transactions, apiData?.open_trade));
  if (!pairs.length) return false;
  downloadCsv(transactionsFilename(symbolName, strategy, executionMode, today, source), buildTransactionsCsv(pairs));
  return true;
};
